import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCase, updateCase } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

export default function EditCasePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    case_number: '',
  });

  useEffect(() => {
    loadCase();
  }, [id]);

  const loadCase = async () => {
    try {
      const res = await getCase(id);
      setFormData({
        title: res.data.title || '',
        description: res.data.description || '',
        client_name: res.data.client_name || '',
        case_number: res.data.case_number || '',
      });
    } catch (error) {
      toast.error('Failed to load case');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateCase(id, formData);
      toast.success('Case updated successfully');
      navigate(`/cases/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update case');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-2xl mx-auto">
          <div className="h-8 w-32 bg-muted animate-pulse rounded mb-6" />
          <div className="h-96 bg-muted animate-pulse rounded-lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid="edit-case-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6 font-body"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Edit Case</CardTitle>
            <CardDescription className="font-body">
              Update case information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="font-body">Case Title *</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="e.g., Smith v. Johnson - Contract Dispute"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="case-title-input"
                  className="font-body"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_name" className="font-body">Client Name *</Label>
                <Input
                  id="client_name"
                  name="client_name"
                  placeholder="e.g., John Smith"
                  value={formData.client_name}
                  onChange={handleChange}
                  required
                  data-testid="client-name-input"
                  className="font-body"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="case_number" className="font-body">Case Number (Optional)</Label>
                <Input
                  id="case_number"
                  name="case_number"
                  placeholder="e.g., 2024-CV-12345"
                  value={formData.case_number}
                  onChange={handleChange}
                  data-testid="case-number-input"
                  className="font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="font-body">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Brief description of the case and its key issues..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="case-description-input"
                  className="font-body resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="font-body"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-navy hover:bg-navy-light font-body flex-1"
                  disabled={saving}
                  data-testid="save-case-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
